import { useState, useEffect, useContext } from 'react';
import apiUser from '../../apiUser';
import { UserContext } from '../../contexts/userProvider';
import LoadingSpinner from '../Loader/load';
import Modal from './Modal';
import TimelineCiclo from './TimelineCiclo';
import SeletorContrato from '../Clientes/SeletorContrato';
import { fmtBRL, fmtComp, fmtDataHora, avisoDe, compParam, StatusBadge, txtRecalcular, txtReenviarMesmo, txtFaturar, nomeContrato } from './format';

// Historico de ciclos de faturamento de um cliente (reutilizado no Painel do
// modulo e na aba Faturamento do Visualizar de Clientes). Requer gerente+.
const HistoricoCliente = ({ idCliente, clienteNome }) => {
    const { setAlert, isAdmin } = useContext(UserContext);
    const [ciclos, setCiclos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [compNovo, setCompNovo] = useState('');
    const [disparando, setDisparando] = useState(false);
    const [contratosModal, setContratosModal] = useState(null); // lista de contratos p/ o modal de seleção (null = fechado)
    const [pendente, setPendente] = useState(null); // { texto, onConfirm }
    const [comentariosCiclo, setComentariosCiclo] = useState(null); // ciclo com o modal de comentários aberto
    const pedir = (texto, fn) => setPendente({ texto, onConfirm: async () => { setPendente(null); await fn(); } });

    // Fecha o modal de comentários e recarrega a lista — o usuário pode ter comentado
    // dentro do modal, e a linha precisa refletir o novo qtd_comentarios.
    const fecharComentarios = () => { setComentariosCiclo(null); carregar(); };

    const aviso = avisoDe(setAlert);

    const carregar = async () => {
        if (!idCliente) return;
        setLoading(true);
        try {
            const { data } = await apiUser.get('/faturamento/ciclosPorCliente', { params: { id_cliente: idCliente } });
            if (data.success) setCiclos(data.dados || []);
            else aviso(data.message || 'Erro ao carregar o histórico de faturamento.');
        } catch { aviso('Erro ao carregar o histórico de faturamento.'); }
        setLoading(false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { carregar(); }, [idCliente]);

    const acao = async (rota, payload) => {
        try {
            const { data } = await apiUser.post(rota, payload);
            aviso(data.message, data.success ? 'success' : 'warning');
            if (data.success) carregar();
        } catch { aviso('Erro ao comunicar com o servidor.'); }
    };

    // Leva 2B: /ciclo/enviar exige id_contrato (Task 7) — um cliente pode ter N
    // contratos. "Reenviar o mesmo" usa o id_contrato do próprio ciclo da linha.
    // "Enviar novo relatório" não tem um ciclo de origem, então precisa perguntar
    // qual contrato quando o cliente tem mais de um (fix wave da review final —
    // ver fix-wave-final-report.md).
    const reenviar = (c) => acao('/faturamento/ciclo/enviar', {
        id_contrato: c.id_contrato, competencia: String(c.competencia).slice(0, 10),
    });
    const atualizarReenviar = (c) => acao('/faturamento/ciclo/atualizarReenviar', { id_ciclo: c.id });
    const faturar = (c) => acao('/faturamento/ciclo/faturar', { id_ciclo: c.id });

    const dispararComContrato = async (idContrato) => {
        setContratosModal(null);
        setDisparando(true);
        await acao('/faturamento/ciclo/enviar', { id_contrato: idContrato, competencia: compParam(compNovo) });
        setDisparando(false);
    };

    // Busca os contratos do cliente e decide: 0 = avisa e não dispara; 1 = dispara
    // direto (auto-skip, mesmo padrão de src/components/Clientes/Itens/index.jsx);
    // 2+ = abre o modal de seleção (SeletorContrato) e espera o usuário escolher.
    const dispararNovo = async () => {
        setDisparando(true);
        try {
            const { data } = await apiUser.get(`/contratos/cliente/${idCliente}`);
            const contratos = data.success ? (data.dados || []) : [];
            if (!contratos.length) {
                aviso('Cliente sem contratos.');
                setDisparando(false);
            } else if (contratos.length === 1) {
                await dispararComContrato(contratos[0].id);
            } else {
                setContratosModal(contratos);
                setDisparando(false);
            }
        } catch {
            aviso('Erro ao buscar os contratos do cliente.');
            setDisparando(false);
        }
    };

    if (loading) return <LoadingSpinner />;

    const btn = 'px-2.5 py-1 rounded-lg text-xs font-semibold text-white';

    return (
        <div className='w-full'>
            {clienteNome && <p className='text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2'>Faturamentos — {clienteNome}</p>}
            <div className='flex items-end justify-between gap-2 flex-wrap mb-3'>
                <div className='flex items-end gap-2'>
                    <div>
                        <label className='block text-xs text-gray-600 dark:text-gray-300 mb-1'>Competência (vazio = mês anterior)</label>
                        <input type='month' value={compNovo} onChange={(e) => setCompNovo(e.target.value)}
                            className='border rounded-lg p-1.5 text-sm dark:bg-gray-700 dark:text-white' />
                    </div>
                    <button onClick={dispararNovo} disabled={disparando}
                        className='px-3 py-1.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50' style={{ backgroundColor: '#9ac31c' }}>
                        {disparando ? 'Enviando...' : 'Enviar relatório'}
                    </button>
                </div>
                <button onClick={carregar} className='px-3 py-1.5 bg-gray-200 dark:bg-gray-600 dark:text-white rounded-lg text-sm'>Atualizar</button>
            </div>
            {pendente && (
                <div className='mb-3 bg-yellow-50 border border-yellow-300 rounded-lg p-3'>
                    <p className='text-sm text-yellow-900 mb-2'>{pendente.texto}</p>
                    <div className='flex gap-2'>
                        <button onClick={pendente.onConfirm} className='px-3 py-1.5 rounded-lg text-white text-sm font-semibold' style={{ backgroundColor: '#9ac31c' }}>Confirmar</button>
                        <button onClick={() => setPendente(null)} className='px-3 py-1.5 bg-gray-200 dark:bg-gray-600 dark:text-white rounded-lg text-sm'>Cancelar</button>
                    </div>
                </div>
            )}
            {!ciclos.length ? (
                <p className='text-sm text-gray-600 dark:text-gray-400 py-4'>Nenhum faturamento para este cliente.</p>
            ) : (
            <div className='overflow-x-auto'>
                <table className='w-full text-sm text-left'>
                    <thead>
                        <tr className='text-xs font-semibold text-gray-600 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600'>
                            <th className='py-2 pr-3'>Contrato</th>
                            <th className='py-2 pr-3'>Competência</th>
                            <th className='py-2 pr-3'>Valor</th>
                            <th className='py-2 pr-3'>Status</th>
                            <th className='py-2 pr-3'>Enviado em</th>
                            <th className='py-2 pr-3'>Prazo</th>
                            <th className='py-2 pr-3'>Respondido por</th>
                            <th className='py-2'>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ciclos.map((c) => {
                            return (
                                <tr key={c.id} className='border-b border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-200 align-top'>
                                    <td className='py-2 pr-3 whitespace-nowrap'>{c.contrato_nome ? nomeContrato(c) : '—'}</td>
                                    <td className='py-2 pr-3 whitespace-nowrap'>{fmtComp(c.competencia)}</td>
                                    <td className='py-2 pr-3 whitespace-nowrap'>{fmtBRL(c.valor_total)}</td>
                                    <td className='py-2 pr-3'>
                                        <StatusBadge ciclo={c} />
                                    </td>
                                    <td className='py-2 pr-3 whitespace-nowrap'>{fmtDataHora(c.enviado_em)}</td>
                                    <td className='py-2 pr-3 whitespace-nowrap'>{fmtDataHora(c.prazo_em)}</td>
                                    <td className='py-2 pr-3'>{c.respondido_por || '—'}</td>
                                    <td className='py-2'>
                                        <div className='flex gap-1.5 flex-wrap'>
                                            {['aguardando', 'contestado', 'erro'].includes(c.status) && (
                                                <button onClick={() => pedir(txtRecalcular(clienteNome), () => atualizarReenviar(c))}
                                                    title={c.status === 'contestado' ? undefined : 'Recalcula os valores a partir dos itens atuais e reenvia ao cliente'}
                                                    className={btn} style={{ backgroundColor: c.status === 'contestado' ? '#9ac31c' : '#f97316' }}>Recalcular valores e reenviar</button>
                                            )}
                                            {['aguardando', 'contestado', 'erro'].includes(c.status) && (
                                                <button onClick={() => pedir(txtReenviarMesmo(c.status, c.valor_total, clienteNome), () => reenviar(c))}
                                                    className={c.status === 'contestado'
                                                        ? 'px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:text-white'
                                                        : `${btn} bg-vox-blue-600 hover:bg-vox-blue-700`}>Reenviar o mesmo</button>
                                            )}
                                            {isAdmin && ['aprovado', 'expirado', 'contestado'].includes(c.status) && (
                                                <button onClick={() => pedir(txtFaturar(c.valor_total, clienteNome), () => faturar(c))}
                                                    className={btn} style={{ backgroundColor: '#9ac31c' }}>{c.status === 'contestado' ? 'Faturar mesmo assim' : 'Faturar'}</button>
                                            )}
                                            <a href={`/faturamento/${c.hash_acesso}`} target='_blank' rel='noreferrer'
                                                className='px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 whitespace-nowrap'>Ver o que o cliente recebe</a>
                                            <button onClick={() => setComentariosCiclo(c)} title='Comentários'
                                                className='relative px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200'>
                                                💬
                                                {c.qtd_comentarios > 0 && (
                                                    <span className='absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-vox-blue-600 text-white text-[10px] font-bold flex items-center justify-center'>
                                                        {c.qtd_comentarios}
                                                    </span>
                                                )}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            )}
            {contratosModal && (
                <Modal title={`Enviar novo relatório — ${clienteNome || 'cliente'}`} onClose={() => setContratosModal(null)}>
                    <SeletorContrato contratos={contratosModal} onSelecionar={(ct) => dispararComContrato(ct.id)} />
                </Modal>
            )}
            {comentariosCiclo && (
                <Modal
                    title={`Comentários — ${clienteNome || comentariosCiclo.cliente_nome || 'cliente'}${comentariosCiclo.contrato_nome ? ` (${nomeContrato(comentariosCiclo)})` : ''}`}
                    onClose={fecharComentarios}
                    footer={<button onClick={fecharComentarios} className='px-4 py-2 bg-gray-200 dark:bg-gray-600 dark:text-gray-200 rounded-lg'>Fechar</button>}
                >
                    <TimelineCiclo idCiclo={comentariosCiclo.id} />
                </Modal>
            )}
        </div>
    );
};

export default HistoricoCliente;
